import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';

import { PublicLayout } from '@/components/layout/public-layout';
import { RouteFallback } from '@/components/route-fallback';

const HomePage = lazy(() => import('@/features/home/home-page'));
const SearchPage = lazy(() => import('@/features/search/search-page'));
const PropertyDetailPage = lazy(() => import('@/features/property/property-detail-page'));
const WishlistPage = lazy(() => import('@/features/engagement/wishlist-page'));
const ComparePage = lazy(() => import('@/features/engagement/compare-page'));
const NotFoundPage = lazy(() => import('@/features/misc/not-found-page'));

const ToolsHubPage = lazy(() => import('@/features/tools/tools-hub-page'));
const EmiCalculatorPage = lazy(() => import('@/features/tools/emi-calculator-page'));
const AffordabilityCalculatorPage = lazy(
  () => import('@/features/tools/affordability-calculator-page'),
);
const RentalYieldCalculatorPage = lazy(() => import('@/features/tools/rental-yield-calculator-page'));
const StampDutyCalculatorPage = lazy(() => import('@/features/tools/stamp-duty-calculator-page'));

const ContactPage = lazy(() => import('@/features/contact/contact-page'));
const CareersPage = lazy(() => import('@/features/careers/careers-page'));
const AboutPage = lazy(() => import('@/features/about/about-page'));
const LeadershipPage = lazy(() => import('@/features/about/leadership-page'));
const VaastuTipsPage = lazy(() => import('@/features/vaastu-tips/vaastu-tips-page'));
const CookiesPage = lazy(() => import('@/features/cookie-consent/cookies-page'));
const PrivacyPage = lazy(() => import('@/features/legal/privacy-page'));
const TermsPage = lazy(() => import('@/features/legal/terms-page'));
const ReraDisclosuresPage = lazy(() => import('@/features/legal/rera-disclosures-page'));
const FaqPage = lazy(() => import('@/features/faq/faq-page'));

const GroupBookingPage = lazy(() => import('@/features/services/group-booking-page'));
const BtsPage = lazy(() => import('@/features/services/bts-page'));
const InvestmentAdvisoryPage = lazy(() => import('@/features/services/investment-advisory-page'));
const MandatePage = lazy(() => import('@/features/services/mandate-page'));


/** Wrap lazy route elements in Suspense with a branded fallback. */
function lazyRoute(element: React.ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    element: (
      <PublicLayout>
        <Outlet />
      </PublicLayout>
    ),
    children: [
      { path: '/', element: lazyRoute(<HomePage />) },
      { path: '/search', element: lazyRoute(<SearchPage />) },
      { path: '/property/:slug', element: lazyRoute(<PropertyDetailPage />) },
      { path: '/wishlist', element: lazyRoute(<WishlistPage />) },
      { path: '/compare', element: lazyRoute(<ComparePage />) },
      {
        path: '/buy',
        element: lazyRoute(<SearchPage heading="Inventory for sale" canonicalPath="/buy" />),
      },
      {
        path: '/rent',
        element: lazyRoute(
          <SearchPage
            heading="Inventory for rent"
            preset={{ transaction: 'rent' }}
            presetKey="transaction"
            canonicalPath="/rent"
          />,
        ),
      },
      {
        path: '/commercial',
        element: lazyRoute(
          <SearchPage
            heading="Commercial inventory"
            preset={{ category: 'commercial' }}
            presetKey="category"
            canonicalPath="/commercial"
          />,
        ),
      },
      {
        path: '/invest',
        element: lazyRoute(
          <SearchPage
            heading="Investment inventory"
            preset={{ preLeased: true }}
            presetKey="preLeased"
            canonicalPath="/invest"
          />,
        ),
      },
      {
        path: '/gift-city',
        element: lazyRoute(
          <SearchPage
            heading="GIFT City inventory"
            preset={{ giftCity: true }}
            presetKey="giftCity"
            canonicalPath="/gift-city"
          />,
        ),
      },
      // SEO landing route for the navbar mega menu. Renders the same
      // SearchPage, which restores filters from the URL and runs the identical
      // search pipeline — the path only sets a clean, crawlable canonical.
      { path: '/inventory', element: lazyRoute(<SearchPage heading="Inventory" canonicalPath="/inventory" />) },
      // Legacy paths, kept as redirects so previously indexed/bookmarked links
      // still resolve instead of 404ing.
      { path: '/properties', element: <Navigate to="/inventory" replace /> },
      { path: '/projects', element: <Navigate to="/inventory" replace /> },
      { path: '/calculators', element: lazyRoute(<ToolsHubPage />) },
      { path: '/calculators/emi', element: lazyRoute(<EmiCalculatorPage />) },
      { path: '/calculators/affordability', element: lazyRoute(<AffordabilityCalculatorPage />) },
      { path: '/calculators/rental-yield', element: lazyRoute(<RentalYieldCalculatorPage />) },
      { path: '/calculators/stamp-duty', element: lazyRoute(<StampDutyCalculatorPage />) },
      { path: '/services/group-booking', element: lazyRoute(<GroupBookingPage />) },
      { path: '/services/built-to-suit', element: lazyRoute(<BtsPage />) },
      { path: '/services/bts', element: <Navigate to="/services/built-to-suit" replace /> },
      { path: '/services/investment-advisory', element: lazyRoute(<InvestmentAdvisoryPage />) },
      { path: '/services/mandate', element: lazyRoute(<MandatePage />) },
      { path: '/contact', element: lazyRoute(<ContactPage />) },
      { path: '/careers', element: lazyRoute(<CareersPage />) },
      { path: '/about', element: lazyRoute(<AboutPage />) },
      { path: '/about/leadership', element: lazyRoute(<LeadershipPage />) },
      { path: '/vaastu-tips', element: lazyRoute(<VaastuTipsPage />) },
      { path: '/cookies', element: lazyRoute(<CookiesPage />) },
      { path: '/privacy', element: lazyRoute(<PrivacyPage />) },
      { path: '/terms', element: lazyRoute(<TermsPage />) },
      { path: '/rera', element: lazyRoute(<ReraDisclosuresPage />) },
      { path: '/faq', element: lazyRoute(<FaqPage />) },
      { path: '*', element: lazyRoute(<NotFoundPage />) },
    ],
  },
]);
